<template>
  <div class="clerk-logs-container">
    <div class="page-header">
      <h2><span class="breadcrumb-level">系统管理</span><span class="breadcrumb-sep">/</span><span class="breadcrumb-level">日志管理</span><span class="breadcrumb-sep">/</span><span class="breadcrumb-level breadcrumb-current">店员日志</span></h2>
      <el-tooltip content="6个月以前的归档数据" placement="top">
        <el-switch
          v-model="isArchived"
          active-text="查看归档"
          inactive-text="当前数据"
          @change="handleArchiveToggle"
        />
      </el-tooltip>
    </div>
    <el-card shadow="hover">
      <div class="card-header">
        <el-input
          v-model="searchQuery"
          placeholder="搜索订单号或操作描述"
          style="width: 280px"
          clearable
          @keyup.enter="handleSearch"
        >
          <template #prefix>
            <el-icon><search /></el-icon>
          </template>
        </el-input>
        <el-input
          v-model="phoneQuery"
          placeholder="店员手机号"
          style="width: 170px"
          clearable
          @keyup.enter="handleSearch"
        >
          <template #prefix>
            <el-icon><phone /></el-icon>
          </template>
        </el-input>
        <el-select
          v-model="filters.action"
          placeholder="操作类型"
          style="width: 150px"
          clearable
        >
          <el-option label="扫码核销" value="scan_verify" />
          <el-option label="完成订单" value="complete_order" />
          <el-option label="创建打卡" value="checkin_create" />
          <el-option label="更新打卡" value="checkin_update" />
          <el-option label="删除打卡" value="checkin_delete" />
        </el-select>
        <el-select
          v-model="filters.order_type"
          placeholder="订单类型"
          style="width: 130px"
          clearable
        >
          <el-option label="酒店" value="hotel" />
          <el-option label="乐园" value="park" />
          <el-option label="日托" value="daycare" />
          <el-option label="活动" value="activity" />
        </el-select>
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          style="width: 280px"
        />
        <el-button type="primary" @click="handleSearch">
          <el-icon><search /></el-icon> 搜索
        </el-button>
        <el-button @click="handleClearFilters">
          <el-icon><refresh-left /></el-icon> 清除筛选
        </el-button>
      </div>

      <el-alert v-if="isArchived" title="当前为历史归档数据" type="info" :closable="false" style="margin-top:12px" />
      <el-alert v-if="!isArchived" title="6个月以前的数据请在右上角切换查看" type="info" :closable="false" style="margin-top:12px" />

      <el-table
        :data="logs"
        border stripe
        style="width: 100%; margin-top: 20px;"
        height="600"
        v-loading="loading"
        @row-click="handleRowClick"
      >
        <el-table-column prop="id" label="ID" width="70" />
        <el-table-column label="店员" width="160">
          <template #default="{ row }">
            <div>{{ row.clerk?.name || row.clerk?.wechat_name || '-' }}</div>
            <div v-if="row.clerk?.phone" style="font-size:12px;color:#999;">{{ row.clerk.phone }}</div>
          </template>
        </el-table-column>
        <el-table-column label="操作类型" width="130">
          <template #default="{ row }">
            <el-tag :type="actionTag(row.action)" size="small">
              {{ actionLabel(row.action) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="order_no" label="订单号" width="200" />
        <el-table-column label="订单类型" width="100">
          <template #default="{ row }">
            {{ orderTypeLabel(row.order_type) }}
          </template>
        </el-table-column>
        <el-table-column prop="description" label="操作描述" min-width="250" show-overflow-tooltip />
        <el-table-column prop="created_at" label="操作时间" width="180">
          <template #default="{ row }">
            {{ formatDateTime(row.created_at) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="80" fixed="right">
          <template #default="{ row }">
            <el-button type="text" @click.stop="handleViewDetail(row)">
              <el-icon><view /></el-icon>
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="card-footer">
        <el-pagination
          :current-page="currentPage"
          :page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 右侧抽屉：订单基础信息 + 日志详情 -->
    <el-drawer
      v-model="drawerVisible"
      title="操作详情"
      size="500px"
      direction="rtl"
    >
      <template v-if="selectedLog">
        <el-descriptions title="基础信息" :column="1" border>
          <el-descriptions-item label="日志ID">{{ selectedLog.id }}</el-descriptions-item>
          <el-descriptions-item label="店员">
            {{ selectedLog.clerk?.name || selectedLog.clerk?.wechat_name || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="操作类型">
            <el-tag :type="actionTag(selectedLog.action)" size="small">
              {{ actionLabel(selectedLog.action) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="操作时间">{{ formatDateTime(selectedLog.created_at) }}</el-descriptions-item>
          <el-descriptions-item label="操作描述">{{ selectedLog.description || '-' }}</el-descriptions-item>
        </el-descriptions>

        <el-divider />

        <el-descriptions v-if="selectedLog.order_info" title="关联订单" :column="1" border>
          <el-descriptions-item label="订单号">{{ selectedLog.order_info.order_no }}</el-descriptions-item>
          <el-descriptions-item label="订单类型">{{ orderTypeLabel(selectedLog.order_info.order_type) }}</el-descriptions-item>
          <el-descriptions-item label="订单状态">{{ orderStatusLabel(selectedLog.order_info.status) }}</el-descriptions-item>
          <el-descriptions-item label="金额">¥{{ selectedLog.order_info.total_amount }}</el-descriptions-item>
          <el-descriptions-item label="联系人">{{ selectedLog.order_info.contact_name || '-' }}</el-descriptions-item>
          <el-descriptions-item label="联系电话">{{ selectedLog.order_info.contact_phone || '-' }}</el-descriptions-item>
          <el-descriptions-item label="下单时间">{{ formatDateTime(selectedLog.order_info.created_at) }}</el-descriptions-item>
        </el-descriptions>
        <el-empty v-else description="未关联订单" />

        <el-divider v-if="selectedLog.extra && Object.keys(selectedLog.extra).length" />
        <el-descriptions v-if="selectedLog.extra && Object.keys(selectedLog.extra).length" title="额外数据" :column="1" border>
          <el-descriptions-item>
            <pre style="margin:0;white-space:pre-wrap;word-break:break-all;">{{ JSON.stringify(selectedLog.extra, null, 2) }}</pre>
          </el-descriptions-item>
        </el-descriptions>
      </template>
    </el-drawer>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import { Search, RefreshLeft, View, Phone } from '@element-plus/icons-vue';
import clerkLogApi from '../../api/clerkLog';

export default {
  name: 'ClerkLogs',
  components: { Search, RefreshLeft, View, Phone },
  setup() {
    const searchQuery = ref('');
    const phoneQuery = ref('');
    const filters = ref({ action: '', order_type: '' });
    const dateRange = ref([]);
    const logs = ref([]);
    const currentPage = ref(1);
    const pageSize = ref(20);
    const total = ref(0);
    const loading = ref(false);
    const isArchived = ref(false);
    const drawerVisible = ref(false);
    const selectedLog = ref(null);

    const fetchLogs = async () => {
      loading.value = true;
      try {
        const params = {
          page: currentPage.value,
          page_size: pageSize.value,
          search: searchQuery.value,
          phone: phoneQuery.value,
          action: filters.value.action,
          order_type: filters.value.order_type,
          start_date: dateRange.value[0] ? dateRange.value[0].toISOString() : '',
          end_date: dateRange.value[1] ? dateRange.value[1].toISOString() : '',
        };
        if (isArchived.value) params.archived = '1';
        const response = await clerkLogApi.getClerkLogs(params);
        logs.value = Array.isArray(response.data) ? response.data : [];
        total.value = response.pagination?.total || response.total || 0;
      } catch (error) {
        ElMessage.error('获取日志失败');
        console.error(error);
        logs.value = [];
        total.value = 0;
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      currentPage.value = 1;
      fetchLogs();
    };

    const handleClearFilters = () => {
      searchQuery.value = '';
      phoneQuery.value = '';
      filters.value.action = '';
      filters.value.order_type = '';
      dateRange.value = [];
      isArchived.value = false;
      currentPage.value = 1;
      fetchLogs();
    };

    const handleArchiveToggle = () => {
      currentPage.value = 1;
      fetchLogs();
    };

    const handleSizeChange = (size) => {
      pageSize.value = size;
      fetchLogs();
    };

    const handleCurrentChange = (page) => {
      currentPage.value = page;
      fetchLogs();
    };

    const handleRowClick = (row) => {
      selectedLog.value = row;
      drawerVisible.value = true;
    };

    const handleViewDetail = (row) => {
      selectedLog.value = row;
      drawerVisible.value = true;
    };

    const actionLabel = (action) => {
      const map = {
        scan_verify: '扫码核销',
        complete_order: '完成订单',
        checkin_create: '创建打卡',
        checkin_update: '更新打卡',
        checkin_delete: '删除打卡',
      };
      return map[action] || action;
    };

    const actionTag = (action) => {
      const map = {
        scan_verify: 'success',
        complete_order: 'primary',
        checkin_create: 'warning',
        checkin_update: 'info',
        checkin_delete: 'danger',
      };
      return map[action] || 'info';
    };

    const orderTypeLabel = (type) => {
      const map = {
        hotel: '酒店',
        park: '乐园',
        daycare: '日托',
        activity: '活动',
        mall: '商城',
        recharge: '充值',
      };
      return map[type] || type || '-';
    };

    const orderStatusLabel = (status) => {
      const map = {
        pending: '待支付',
        paid: '已支付',
        in_service: '服务中',
        completed: '已完成',
        cancelled: '已取消',
        refunded: '已退款',
      };
      return map[status] || status || '-';
    };

    const formatDateTime = (dateString) => {
      if (!dateString) return '-';
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return dateString;
      return d.toLocaleString('zh-CN', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      });
    };

    onMounted(fetchLogs);

    return {
      searchQuery, phoneQuery, filters, dateRange, logs,
      currentPage, pageSize, total, loading,
      drawerVisible, selectedLog, isArchived,
      handleSearch, handleClearFilters, handleArchiveToggle,
      handleSizeChange, handleCurrentChange,
      handleRowClick, handleViewDetail,
      actionLabel, actionTag, orderTypeLabel, orderStatusLabel, formatDateTime,
    };
  },
};
</script>

<style scoped>


.clerk-logs-container h2 {
  margin: 0;
  color: #333;
}
.card-header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}
.card-footer {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}
.el-descriptions {
  margin-bottom: 10px;
}
pre {
  background: #f5f7fa;
  padding: 12px;
  border-radius: 4px;
  max-height: 300px;
  overflow: auto;
}
</style>
